import React, { useEffect, useState } from "react";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend,
  ResponsiveContainer, CartesianGrid, ReferenceLine,
} from "recharts";

const API = process.env.REACT_APP_BACKEND_URL || "";
const AUTH_KEY = "eviq_admin_token";
const authHeaders = () => {
  const t = localStorage.getItem(AUTH_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const usd = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

export const ExpenseVsRevenueCard = () => {
  const [data, setData] = useState(null);
  const [months, setMonths] = useState(12);

  useEffect(() => {
    fetch(`${API}/api/admin/expense-vs-revenue?months=${months}`, {
      credentials: "include", headers: authHeaders(),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .catch(() => {});
  }, [months]);

  const t = data?.totals;
  const netColor = (t?.net ?? 0) >= 0 ? "#39FF14" : "#FF5C5C";

  return (
    <div data-testid="expense-vs-revenue-section" className="rounded-3xl border border-white/10 bg-white/[0.02] p-5 sm:p-6">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">
          Expense vs revenue · net position
        </div>
        <select value={months} onChange={(e) => setMonths(Number(e.target.value))}
                data-testid="evr-months-select"
                className="appearance-none bg-black border border-white/10 rounded-lg px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider text-white cursor-pointer">
          {[6, 12, 24, 36].map((m) => <option key={m} value={m} className="bg-black">{m} mo</option>)}
        </select>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-3">
          <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Revenue ({months}mo)</div>
          <div className="text-lg font-semibold" style={{ color: "#39FF14" }} data-testid="evr-revenue-total">{usd(t?.revenue)}</div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-3">
          <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Expenses ({months}mo)</div>
          <div className="text-lg font-semibold" style={{ color: "#FF5C5C" }} data-testid="evr-expenses-total">{usd(t?.expenses)}</div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-3">
          <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Net</div>
          <div className="text-lg font-semibold" style={{ color: netColor }} data-testid="evr-net-total">{usd(t?.net)}</div>
        </div>
      </div>

      <div className="h-64">
        {data?.rows?.length ? (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data.rows}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#71717a" }} />
              <YAxis tick={{ fontSize: 10, fill: "#71717a" }} tickFormatter={(v) => `$${v}`} />
              <Tooltip
                contentStyle={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, fontSize: 12 }}
                formatter={(v, name) => [usd(v), name]}
              />
              <ReferenceLine y={0} stroke="rgba(255,255,255,0.25)" />
              <Bar dataKey="revenue" name="Revenue" fill="#39FF14" fillOpacity={0.75} radius={[3, 3, 0, 0]} />
              <Bar dataKey="expenses" name="Expenses" fill="#FF5C5C" fillOpacity={0.75} radius={[3, 3, 0, 0]} />
              <Line dataKey="net" name="Net" stroke="#00E5FF" strokeWidth={2} dot={{ r: 2 }} />
              <Legend wrapperStyle={{ fontSize: 10, color: "#999" }} />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-zinc-600 text-xs">Loading…</div>
        )}
      </div>
      <p className="mt-3 text-[11px] text-zinc-500 leading-relaxed">
        Cash-basis: paid subscriptions + certified-report sales vs your logged operating
        expenses (manual, imported, auto-logged and Gmail-synced Emergent charges).
      </p>
    </div>
  );
};
